import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  adminCreateGiftBrandSeller,
  adminGiftBrandSellerList,
  adminUpdateUser,
} from '@shared/api/user/user.controller';
import { User } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';

const UPSERT_MODAL_OPEN_ID = 'gift-brand-seller-user-upsert';

//* populate된 담당 브랜드 목록 추출 (신 giftItemBrands 우선, 구 giftItemBrand 폴백)
const brandsOf = (user: User): { id: string; name: string }[] => {
  const list =
    user.giftItemBrands ?? (user.giftItemBrand ? [user.giftItemBrand] : []);
  return list.map((brand) =>
    typeof brand === 'string'
      ? { id: brand, name: brand }
      : { id: brand._id, name: brand.name ?? brand._id },
  );
};

const brandIdsOf = (user: User): string[] => brandsOf(user).map((b) => b.id);

interface UpsertFormInputs {
  username: string;
  password: string;
  name: string;
  phoneNumber: string;
}

interface UpsertModalProps {
  target: User | null; // null이면 생성 모드
  onSaved: () => void;
}

const GiftBrandSellerUpsertModal: FC<UpsertModalProps> = ({ target, onSaved }) => {
  const notiInstance = useRecoilValue(notificationInstanceAtom);
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(UPSERT_MODAL_OPEN_ID));
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  const editMode = !!target;

  const { register, handleSubmit, reset } = useForm<UpsertFormInputs>();
  // 담당 브랜드는 복수 선택이라 react-hook-form 대신 로컬 상태로 관리
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);

  const toggleBrand = useCallback((id: string) => {
    setSelectedBrandIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  }, []);

  // target 변경 시 form 동기화
  useEffect(() => {
    reset({
      username: target?.username ?? '',
      password: '',
      name: target?.info?.name ?? '',
      phoneNumber: target?.info?.phoneNumber ?? '',
    });
    setSelectedBrandIds(target ? brandIdsOf(target) : []);
  }, [target, reset]);

  const [{ loading }, submit] = useAsyncFn(
    async (data: UpsertFormInputs) => {
      if (selectedBrandIds.length === 0) {
        notificateError(notiInstance, '담당 브랜드를 1개 이상 선택해주세요.');
        return;
      }
      try {
        if (editMode && target) {
          await adminUpdateUser({
            _id: target._id,
            giftItemBrands: selectedBrandIds,
            ...(data.password ? { password: data.password } : {}),
            info: {
              ...(target.info ?? {}),
              name: data.name,
              phoneNumber: data.phoneNumber,
            },
          });
        } else {
          if (!data.username || !data.password) {
            notificateError(notiInstance, '아이디와 비밀번호를 입력해주세요.');
            return;
          }
          await adminCreateGiftBrandSeller({
            username: data.username,
            password: data.password,
            name: data.name,
            phoneNumber: data.phoneNumber,
            giftItemBrands: selectedBrandIds,
          });
        }
        notificateSuccess(notiInstance, '저장 완료');
        setOpen(false);
        onSaved();
      } catch (err) {
        notificateError(notiInstance, '저장 실패 (아이디 중복 여부를 확인해주세요)');
        console.error(err);
      }
    },
    [editMode, target, notiInstance, onSaved, setOpen, selectedBrandIds],
  );

  const onSubmit: SubmitHandler<UpsertFormInputs> = (data) => submit(data);

  return (
    <Modal openId={UPSERT_MODAL_OPEN_ID}>
      <form className="space-y-4 pt-2" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="text-16 font-bold">
          {editMode ? '배송관리자 수정' : '배송관리자 등록'}
        </h2>
        <div className="grid grid-cols-1 gap-3">
          {editMode ? (
            <div className="flex items-center gap-2 text-14">
              <span className="font-semibold">아이디</span>
              <span>{target?.username}</span>
            </div>
          ) : (
            <TextField
              {...register('username')}
              label="아이디"
              placeholder="로그인 아이디"
              autoComplete="off"
              className="w-full"
            />
          )}

          <div className="flex flex-col gap-1">
            <label className="text-14 font-semibold">
              담당 브랜드 <span className="text-12 text-gray-400">(복수 선택 가능)</span>
            </label>
            <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded border p-2">
              {(brandList ?? []).length === 0 ? (
                <span className="text-12 text-gray-400">브랜드 목록을 불러오는 중…</span>
              ) : (
                (brandList ?? []).map((brand) => (
                  <label
                    key={brand._id}
                    className="flex cursor-pointer items-center gap-2 text-14"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBrandIds.includes(brand._id)}
                      onChange={() => toggleBrand(brand._id)}
                    />
                    <span>{brand.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <TextField
            {...register('name')}
            label="이름"
            placeholder="담당자 이름"
            className="w-full"
          />
          <TextField
            {...register('phoneNumber')}
            label="전화번호"
            placeholder="전화번호"
            className="w-full"
          />
          <TextField
            {...register('password')}
            label={editMode ? '새 비밀번호 (변경 시에만 입력)' : '비밀번호'}
            placeholder="비밀번호"
            type="password"
            autoComplete="new-password"
            className="w-full"
          />
        </div>
        <div className="pt-2">
          <Button
            disabled={loading}
            type="submit"
            className="button-rectangle h-10 w-full rounded-md theme-text-3 theme-bg-main"
          >
            저장
          </Button>
        </div>
      </form>
      <LoadingModal open={loading} title="저장중" />
    </Modal>
  );
};

export const GiftBrandSellerUserPage: FC = function GiftBrandSellerUserPage() {
  const { isAdmin, sellerRole } = useAuth();
  const setUpsertOpen = useSetRecoilState(simpleOpenAtomFamily(UPSERT_MODAL_OPEN_ID));
  const [editTarget, setEditTarget] = useState<User | null>(null);

  // 담당 브랜드 선택용 목록 로드
  useGiftItemBrandListHook(sellerRole());
  const brandStatus = useRecoilValue(giftItemBrandListStatusAtom);

  const [{ loading, value: userList }, fetchUsers] = useAsyncFn(
    async () => adminGiftBrandSellerList(),
    [],
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreate = useCallback(() => {
    setEditTarget(null);
    setUpsertOpen(true);
  }, [setUpsertOpen]);

  const openEdit = useCallback(
    (user: User) => {
      setEditTarget(user);
      setUpsertOpen(true);
    },
    [setUpsertOpen],
  );

  const rows = useMemo(() => userList ?? [], [userList]);

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center text-gray-500">
        관리자 전용 기능입니다.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">배송관리자 계정 관리</h1>
            <p className="mt-1 text-sm text-gray-500">
              답례품 브랜드별 배송관리자(GiftBrandSeller) 계정을 관리합니다.
            </p>
          </div>
          <Button
            onClick={openCreate}
            disabled={brandStatus !== 'success'}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            + 배송관리자 등록
          </Button>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          전체 <span className="font-medium text-gray-900">{rows.length}</span>명
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="center-box h-screen-15">
            <Loading />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            등록된 배송관리자가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="w-full min-w-[640px] table-auto border-collapse text-14">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-3 py-2 text-left">아이디</th>
                  <th className="px-3 py-2 text-left">이름</th>
                  <th className="px-3 py-2 text-left">전화번호</th>
                  <th className="px-3 py-2 text-left">담당 브랜드</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user._id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2">{user.username}</td>
                    <td className="px-3 py-2">{user.info?.name}</td>
                    <td className="px-3 py-2">{user.info?.phoneNumber}</td>
                    <td className="px-3 py-2">
                      {brandsOf(user).length === 0 ? (
                        <span className="text-red-500">미지정</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {brandsOf(user).map((b) => (
                            <span
                              key={b.id}
                              className="rounded bg-indigo-50 px-2 py-0.5 text-indigo-700"
                            >
                              {b.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        onClick={() => openEdit(user)}
                        className="rounded border px-2 py-1 text-12 theme-bg-1"
                      >
                        수정
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GiftBrandSellerUpsertModal target={editTarget} onSaved={fetchUsers} />
    </div>
  );
};
